import type { TankConfig, TankSpecial, TankFamilyId } from "../types/tanks";
import { TANK_CATALOG } from "./tankCatalog.data";

/**
 * Catálogo de tanques do Rocketanks.
 *
 * Os 149 tanques compráveis vivem em tankCatalog.data.ts — um snapshot
 * canônico e determinístico gerado por scripts/generate-tank-catalog.ts.
 * Antes, os stats eram sorteados com Math.random() a cada carregamento:
 * o tanque comprado mudava de raridade/dano/HP entre sessões e entre
 * dispositivos. Agora o catálogo é imutável e versionado junto do código.
 */

const FAMILY_LABELS: Record<TankFamilyId, string> = {
  predator: "Linhagem Predadora",
  colossus: "Linhagem Colosso",
  venom: "Linhagem Peçonhenta",
  storm: "Linhagem Tempestade",
  phantom: "Linhagem Espectral",
  bulwark: "Linhagem Bastião",
  cosmic: "Linhagem Cósmica",
  mythic: "Linhagem Mítica",
};

const SPECIAL_LABELS: Record<TankSpecial, string> = {
  shield: "blindagem reativa",
  stealth: "camuflagem ativa",
  regen: "regeneração de casco",
  explosive: "projéteis explosivos",
  piercing: "projéteis perfurantes",
  homing: "projéteis teleguiados",
  rebirth: "renascimento da Fênix",
  burn: "sopro incendiário do Dragão",
};

function describeTank(tank: TankConfig): string {
  const parts: string[] = [];

  if (tank.family) parts.push(FAMILY_LABELS[tank.family]);

  if (tank.scale && tank.scale <= 0.8) parts.push("compacto e ágil");
  else if (tank.scale && tank.scale >= 1.45) parts.push("massivo e imponente");

  if (tank.bulletCount === 2) parts.push("disparo duplo");
  else if (tank.bulletCount === 3) parts.push("disparo triplo");
  else if ((tank.bulletCount ?? 1) >= 4) parts.push("disparo quádruplo");

  if (tank.fireRate && tank.fireRate > 1.25) parts.push("cadência rápida");
  else if (tank.fireRate && tank.fireRate < 0.75) parts.push("tiros potentes");

  if (tank.special) {
    const power = (tank.specialPower ?? 1) > 1 ? " reforçados" : "";
    parts.push(`${SPECIAL_LABELS[tank.special]}${power}`.replace("reativa reforçados", "reativa reforçada"));
  }

  return parts.length > 0
    ? `${parts.join(" — ")}.`
    : "Tanque versátil para combate.";
}

const STANDARD_TANK: TankConfig = {
  id: "standard",
  name: "KITSUNE-00",
  description:
    "O lendário chassi criado por Ayame Ken. Equilibrado e confiável — seu primeiro companheiro de batalha.",
  color: "#00CED1",
  speed: 14,
  damage: 25,
  health: 100,
  price: 0,
  currency: "chips",
  rarity: "common",
  unlocked: true,
  bulletCount: 1,
  scale: 1.0,
  fireRate: 1.0,
  bulletSpeed: 1.0,
  family: "predator",
};

export function generateAllTanks(): TankConfig[] {
  const catalogTanks: TankConfig[] = TANK_CATALOG.map((entry) => {
    const tank: TankConfig = {
      ...entry,
      currency: "chips",
      unlocked: false,
      description: "",
    };
    tank.description = describeTank(tank);
    return tank;
  });

  return [STANDARD_TANK, ...catalogTanks];
}

export const allTanks = generateAllTanks();
